/**
 * Vietnamese Parameter Display Name Utility
 *
 * This utility provides Vietnamese translations for all health parameters
 * while maintaining the same parameter keys for data consistency.
 *
 * Maps standardized parameter keys to Vietnamese display names.
 */

// Vietnamese parameter display name mappings
const PARAMETER_DISPLAY_NAMES_VI = {
  // === VITALS ===
  'vitals_temp_c': 'Nhiệt độ',
  'vitals_sbp': 'Huyết áp tâm thu',
  'vitals_dbp': 'Huyết áp tâm trương',
  'vitals_rhr': 'Nhịp tim nghỉ',
  'vitals_rr': 'Tần số hô hấp',
  'vitals_map': 'Huyết áp trung bình',
  'vitals_pp': 'Áp lực mạch',
  'vitals_spo2': 'Độ bão hòa oxy',
  'vitals_sirs': 'Điểm SIRS',
  'vitals_hrv': 'Biến thiên nhịp tim',
  'vitals_vo2max': 'VO₂ Max',
  'vitals_pain': 'Thang đo đau',
  'vitals_weight': 'Cân nặng',
  'vitals_height': 'Chiều cao',
  'vitals_bmi': 'Chỉ số khối cơ thể',

  // === BIOMETRICS - COGNITION ===
  'biometrics_cognition_tmtA_time': 'Bài kiểm tra vạch đường A - Thời gian',
  'biometrics_cognition_tmtA_errors': 'Bài kiểm tra vạch đường A - Lỗi',
  'biometrics_cognition_tmtB_time': 'Bài kiểm tra vạch đường B - Thời gian',
  'biometrics_cognition_tmtB_errors': 'Bài kiểm tra vạch đường B - Lỗi',
  'biometrics_cognition_wordRecall_immediate1': 'Nhớ từ - Thử nghiệm ngay lập tức 1',
  'biometrics_cognition_wordRecall_immediate2': 'Nhớ từ - Thử nghiệm ngay lập tức 2',
  'biometrics_cognition_wordRecall_delayed': 'Nhớ từ - Trì hoãn',
  'biometrics_cognition_stroop_word_time': 'Bài kiểm tra Stroop - Thời gian từ',
  'biometrics_cognition_stroop_word_errors': 'Bài kiểm tra Stroop - Lỗi từ',
  'biometrics_cognition_stroop_color_time': 'Bài kiểm tra Stroop - Thời gian màu',
  'biometrics_cognition_stroop_color_errors': 'Bài kiểm tra Stroop - Lỗi màu',
  'biometrics_cognition_stroop_incongruent_time': 'Bài kiểm tra Stroop - Thời gian không phù hợp',
  'biometrics_cognition_stroop_incongruent_errors': 'Bài kiểm tra Stroop - Lỗi không phù hợp',
  'biometrics_cognition_stroop_interference_time': 'Bài kiểm tra Stroop - Thời gian can thiệp',
  'biometrics_cognition_stroop_interference_errors': 'Bài kiểm tra Stroop - Lỗi can thiệp',

  // === BIOMETRICS - BODY COMPOSITION ===
  'biometrics_body_fat_percentage': 'Tỷ lệ mỡ cơ thể',
  'biometrics_body_muscle_mass': 'Khối lượng cơ',
  'biometrics_body_bone_density': 'Mật độ xương',
  'biometrics_body_visceral_fat': 'Mỡ nội tạng',
  'biometrics_body_water_percentage': 'Tỷ lệ nước',

  // === BIOMETRICS - BALANCE ===
  'biometrics_balance_ybt_anterior_left': 'YBT Trước - Trái',
  'biometrics_balance_ybt_anterior_right': 'YBT Trước - Phải',
  'biometrics_balance_ybt_posteromedial_left': 'YBT Sau giữa - Trái',
  'biometrics_balance_ybt_posteromedial_right': 'YBT Sau giữa - Phải',
  'biometrics_balance_ybt_posterolateral_left': 'YBT Sau ngoài - Trái',
  'biometrics_balance_ybt_posterolateral_right': 'YBT Sau ngoài - Phải',
  'biometrics_balance_ybt_composite_left': 'YBT Tổng hợp - Trái',
  'biometrics_balance_ybt_composite_right': 'YBT Tổng hợp - Phải',
  'biometrics_balance_ybt_right_limb_length': 'YBT Chiều dài chi - Phải',
  'biometrics_balance_ybt_left_limb_length': 'YBT Chiều dài chi - Trái',
  'biometrics_balance_ybt_right_composite_reach': 'YBT Tầm với tổng hợp - Phải',
  'biometrics_balance_ybt_left_composite_reach': 'YBT Tầm với tổng hợp - Trái',
  'biometrics_balance_ybt_composite_deviation': 'YBT Độ lệch tổng hợp',
  'biometrics_balance_ybt_anterior_deviation': 'YBT Độ lệch trước',
  'biometrics_balance_ybt_posteromedial_deviation': 'YBT Độ lệch sau trong',
  'biometrics_balance_ybt_posterolateral_deviation': 'YBT Độ lệch sau ngoài',
  'biometrics_balance_ybt_performance_ratio': 'YBT Tỷ lệ hiệu suất',

  // === BIOMETRICS - VISION ===
  'biometrics_vision_visualAcuity_distance_right': 'Thị lực - Khoảng cách mắt phải',
  'biometrics_vision_visualAcuity_distance_left': 'Thị lực - Khoảng cách mắt trái',
  'biometrics_vision_visualAcuity_distance_both': 'Thị lực - Khoảng cách cả hai mắt',
  'biometrics_vision_visualAcuity_near_right': 'Thị lực - Gần mắt phải',
  'biometrics_vision_visualAcuity_near_left': 'Thị lực - Gần mắt trái',
  'biometrics_vision_visualAcuity_near_both': 'Thị lực - Gần cả hai mắt',
  'biometrics_vision_visualAcuity_pinhole_right': 'Thị lực - Lỗ kim mắt phải',
  'biometrics_vision_visualAcuity_pinhole_left': 'Thị lực - Lỗ kim mắt trái',

  // === LABS - CBC ===
  'labs_cbc_wbc': 'Số lượng bạch cầu',
  'labs_cbc_neut': 'Bạch cầu trung tính',
  'labs_cbc_lymph': 'Bạch cầu lympho',
  'labs_cbc_mono': 'Bạch cầu đơn nhân',
  'labs_cbc_eos': 'Bạch cầu ái toan',
  'labs_cbc_baso': 'Bạch cầu ái kiềm',
  'labs_cbc_neut_pct': 'Bạch cầu trung tính (%)',
  'labs_cbc_lymph_pct': 'Bạch cầu lympho (%)',
  'labs_cbc_mono_pct': 'Bạch cầu đơn nhân (%)',
  'labs_cbc_eos_pct': 'Bạch cầu ái toan (%)',
  'labs_cbc_baso_pct': 'Bạch cầu ái kiềm (%)',
  'labs_cbc_rbc': 'Số lượng hồng cầu',
  'labs_cbc_hgb': 'Hemoglobin',
  'labs_cbc_hct': 'Hematocrit',
  'labs_cbc_mcv': 'Thể tích trung bình hồng cầu',
  'labs_cbc_mch': 'Hemoglobin trung bình hồng cầu',
  'labs_cbc_mchc': 'Nồng độ Hemoglobin trung bình hồng cầu',
  'labs_cbc_rdw': 'Độ rộng phân bố hồng cầu',
  'labs_cbc_rdw_sd': 'Độ rộng phân bố hồng cầu-SD',
  'labs_cbc_plt': 'Số lượng tiểu cầu',
  'labs_cbc_mpv': 'Thể tích trung bình tiểu cầu',
  'labs_cbc_pdw': 'Độ rộng phân bố tiểu cầu',
  'labs_cbc_pct': 'Tỷ lệ tiểu cầu',
  'labs_cbc_p_lcr': 'Tỷ lệ tiểu cầu lớn',
  'labs_cbc_nlr': 'Tỷ lệ bạch cầu trung tính/lympho',
  // Legacy mappings for backward compatibility
  'labs_cbc_hemoglobin': 'Hemoglobin',
  'labs_cbc_hematocrit': 'Hematocrit',
  'labs_cbc_platelets': 'Tiểu cầu',

  // === LABS - LIPID ===
  'labs_lipid_chol_total': 'Cholesterol tổng',
  'labs_lipid_chol_hdl': 'HDL Cholesterol',
  'labs_lipid_chol_ldl': 'LDL Cholesterol',
  'labs_lipid_triglycerides': 'Triglyceride',
  'labs_lipid_chol_nonhdl': 'Cholesterol không HDL',
  'labs_lipid_ratio_total_hdl': 'Tỷ lệ Cholesterol tổng/HDL',
  'labs_lipid_ratio_ldl_hdl': 'Tỷ lệ LDL/HDL',

  // === LABS - GLUCOSE ===
  'labs_glucose_fasting': 'Đường huyết lúc đói',
  'labs_glucose_random': 'Đường huyết ngẫu nhiên',
  'labs_glucose_hba1c': 'HbA1c',
  'labs_glucose_ogtt_2h': 'OGTT 2 giờ',

  // === LABS - RENAL ===
  'labs_renal_creatinine': 'Creatinine',
  'labs_renal_bun': 'BUN',
  'labs_renal_egfr': 'eGFR',
  'labs_renal_bun_creatinine_ratio': 'Tỷ lệ BUN/Creatinine',

  // === LABS - ELECTROLYTES ===
  'labs_electrolytes_sodium': 'Natri',
  'labs_electrolytes_potassium': 'Kali',
  'labs_electrolytes_chloride': 'Clorua',
  'labs_electrolytes_co2': 'CO2',
  'labs_electrolytes_anion_gap': 'Khoảng cách anion',

  // === LABS - HEPATOLOGY ===
  'labs_hepatology_alt': 'ALT',
  'labs_hepatology_ast': 'AST',
  'labs_hepatology_alp': 'ALP',
  'labs_hepatology_bilirubin_total': 'Bilirubin tổng',
  'labs_hepatology_bilirubin_direct': 'Bilirubin trực tiếp',
  'labs_hepatology_albumin': 'Albumin',

  // === LABS - THYROID ===
  'labs_thyroid_tsh': 'TSH',
  'labs_thyroid_t4_free': 'T4 tự do',
  'labs_thyroid_t3_free': 'T3 tự do',

  // === LABS - INFLAMMATION ===
  'labs_inflammation_crp_hs': 'CRP độ nhạy cao',
  'labs_inflammation_esr': 'Tốc độ lắng hồng cầu',

  // === LABS - CARDIAC ===
  'labs_cardiac_troponin_i': 'Troponin I',
  'labs_cardiac_bnp': 'BNP',
  'labs_cardiac_nt_probnp': 'NT-proBNP',

  // === LABS - VITAMINS ===
  'labs_vitamins_d25': 'Vitamin D 25-OH',
  'labs_vitamins_b12': 'Vitamin B12',
  'labs_vitamins_folate': 'Folate',

  // === PHYSICAL EXAM ===
  // Integumentary System
  'pe_integumentary_alopecia': 'Rụng tóc',
  'pe_integumentary_scalp_lesions': 'Tổn thương da đầu',
  'pe_integumentary_arm_lesions': 'Tổn thương da tay',
  'pe_integumentary_leg_lesions': 'Tổn thương da chân',
  'pe_integumentary_finger_nails': 'Móng tay',
  'pe_integumentary_toe_nails': 'Móng chân',
  'pe_integumentary_jaundice': 'Vàng da',
  'pe_integumentary_pallor': 'Nhợt nhạt',
  'pe_integumentary_bruising': 'Bầm tím',
  'pe_integumentary_surgical_scar': 'Sẹo phẫu thuật',
  'pe_integumentary_caput_medusa': 'Caput medusa',
  'pe_integumentary_skin_turgor': 'Độ đàn hồi da',
  'pe_integumentary_skin_color': 'Màu da',
  'pe_integumentary_skin_texture': 'Kết cấu da',
  'pe_integumentary_lesions': 'Tổn thương da',

  // === PHYSICAL EXAM - LYMPH NODES ===
  'pe_lymph_occipital_nodes': 'Hạch chẩm',
  'pe_lymph_periauricular_nodes': 'Hạch quanh tai',
  'pe_lymph_cervical_nodes': 'Hạch cổ',
  'pe_lymph_submental_nodes': 'Hạch dưới cằm',
  'pe_lymph_submandibular_nodes': 'Hạch dưới hàm',
  'pe_lymph_supraclavicular_nodes': 'Hạch trên xương đòn',
  'pe_lymph_femoral_nodes': 'Hạch đùi',
  'pe_lymph_popliteal_nodes': 'Hạch khoeo',
  'pe_lymph_cervical': 'Hạch cổ',
  'pe_lymph_axillary': 'Hạch nách',
  'pe_lymph_inguinal': 'Hạch bẹn',

  // === PHYSICAL EXAM - HEENT EYES ===
  'pe_heent_eyes_conjunctiva': 'Kết mạc',
  'pe_heent_eyes_sclera': 'Củng mạc',
  'pe_heent_eyes_ocular_shape': 'Hình dạng mắt',
  'pe_heent_eyes_vision_field': 'Trường nhìn',
  'pe_heent_eyes_light_reflex': 'Phản xạ ánh sáng',
  'pe_heent_eyes_red_reflex': 'Phản xạ đỏ',
  'pe_heent_eyes_h_test': 'Test H (chuyển động mắt)',
  'pe_heent_eyes_finger_to_nose': 'Test ngón tay chạm mũi',
  'pe_heent_eyes_pupils': 'Đồng tử',
  'pe_heent_eyes_vision': 'Thị lực',

  // === PHYSICAL EXAM - HEENT EARS ===
  'pe_heent_ears_external_ear_canal': 'Ống tai ngoài',
  'pe_heent_ears_tympanic_membrane': 'Màng nhĩ',
  'pe_heent_ears_whisper_test': 'Test thì thầm',
  'pe_heent_ears_rinne_test': 'Test Rinne',
  'pe_heent_ears_weber_test': 'Test Weber',
  'pe_heent_ears_hearing': 'Thính lực',

  // === PHYSICAL EXAM - HEENT NOSE/SINUS ===
  'pe_heent_nose_breathing': 'Hô hấp qua mũi',
  'pe_heent_nose_nasal_turbinate': 'Cuốn mũi',
  'pe_heent_nose_nasal_polyp': 'Polyp mũi',
  'pe_heent_nose_nasal_septum_deviation': 'Lệch vách ngăn mũi',
  'pe_heent_nose_maxillary_sinus': 'Xoang hàm trên',
  'pe_heent_nose_frontal_sinus': 'Xoang trán',

  // === PHYSICAL EXAM - HEENT MOUTH/THROAT ===
  'pe_heent_mouth_oral_mucosa': 'Niêm mạc miệng',
  'pe_heent_mouth_san_mieng': 'Sàn miệng',
  'pe_heent_mouth_vom_mieng': 'Vòm miệng',
  'pe_heent_mouth_luoi': 'Lưỡi',
  'pe_heent_mouth_amidan': 'Amidan',
  'pe_heent_mouth_mallampati': 'Phân loại Mallampati',
  'pe_heent_mouth_uvula_position': 'Vị trí lưỡi gà',
  'pe_heent_mouth_tmj_tenderness': 'Đau khớp thái dương hàm',
  'pe_heent_mouth_maxillary_teeth': 'Răng hàm trên',
  'pe_heent_mouth_mandibular_teeth': 'Răng hàm dưới',
  'pe_heent_mouth_cough_reflex': 'Phản xạ ho',
  'pe_heent_mouth_teeth': 'Răng',

  // === PHYSICAL EXAM - NECK ===
  'pe_neck_thyroid': 'Tuyến giáp',

  // === PHYSICAL EXAM - CHEST/RESPIRATORY ===
  'pe_chest_resp_chest_expansion_symmetry': 'Đối xứng giãn nở ngực',
  'pe_chest_resp_chest_expansion_range': 'Phạm vi giãn nở ngực',
  'pe_chest_resp_crackles': 'Ran ẩm',
  'pe_chest_resp_wheezing': 'Ran khô',
  'pe_chest_resp_rhonchi': 'Ran thô',
  'pe_chest_resp_stridor': 'Ran rít',
  'pe_chest_resp_sounds': 'Âm thở',
  'pe_chest_resp_effort': 'Nỗ lực hô hấp',

  // === PHYSICAL EXAM - CARDIOVASCULAR ===
  'pe_cardio_heart_rhythm': 'Nhịp tim',
  'pe_cardio_s1': 'Âm tim S1',
  'pe_cardio_s2': 'Âm tim S2',
  'pe_cardio_s3': 'Âm tim S3',
  'pe_cardio_s4': 'Âm tim S4',
  'pe_cardio_systolic_murmur': 'Tiếng thổi tâm thu',
  'pe_cardio_diastolic_murmur': 'Tiếng thổi tâm trương',
  'pe_cardio_apical_impulse': 'Xung động đỉnh tim',
  'pe_cardio_carotid_bruit': 'Tiếng thổi động mạch cảnh',
  'pe_cardio_hepatojugular_reflux': 'Phản xạ gan-tĩnh mạch cổ',
  'pe_cardio_radial_pulse': 'Mạch quay',
  'pe_cardio_cap_refill_finger': 'Thời gian tưới máu ngón tay',
  'pe_cardio_abdominal_bruit': 'Tiếng thổi bụng',
  'pe_cardio_posterior_tibial': 'Mạch chày sau',
  'pe_cardio_dorsalis_pedis': 'Mạch mu bàn chân',
  'pe_cardio_jvd': 'Giãn tĩnh mạch cổ',
  'pe_cardio_peripheral_pulses': 'Mạch ngoại biên',
  'pe_cardio_pedal_edema': 'Phù bàn chân',
  'pe_cardio_cap_refill_toe': 'Thời gian tưới máu ngón chân',
  'pe_cardio_varicose_veins': 'Giãn tĩnh mạch',
  'pe_cardio_heart_sounds': 'Âm tim',

  // === PHYSICAL EXAM - ABDOMEN ===
  'pe_abdomen_bowel_sound': 'Âm ruột',
  'pe_abdomen_bowel_sounds': 'Âm ruột',
  'pe_abdomen_abdominal_distension': 'Chướng bụng',
  'pe_abdomen_hepatomegaly': 'Gan to',
  'pe_abdomen_splenomegaly': 'Lách to',
  'pe_abdomen_epigastric_tenderness': 'Đau vùng thượng vị',
  'pe_abdomen_luq_tenderness': 'Đau góc phần tư trên trái',
  'pe_abdomen_ruq_tenderness': 'Đau góc phần tư trên phải',
  'pe_abdomen_llq_tenderness': 'Đau góc phần tư dưới trái',
  'pe_abdomen_rlq_tenderness': 'Đau góc phần tư dưới phải',
  'pe_abdomen_suprapubic_tenderness': 'Đau vùng trên xương mu',
  'pe_abdomen_cva_tenderness': 'Đau góc sườn-cột sống',
  'pe_abdomen_tenderness': 'Đau bụng',

  // === PHYSICAL EXAM - MUSCULOSKELETAL ===
  'pe_msk_cervical_lordosis': 'Cong cổ tử cung',
  'pe_msk_thoracic_kyphosis': 'Gù lưng ngực',
  'pe_msk_lumbar_lordosis': 'Cong thắt lưng',
  'pe_msk_visible_scoliosis': 'Vẹo cột sống nhìn thấy',
  'pe_msk_cervical_tenderness': 'Đau cổ',
  'pe_msk_thoracic_tenderness': 'Đau lưng ngực',
  'pe_msk_lumbar_tenderness': 'Đau thắt lưng',
  'pe_msk_cervical_flexion': 'Gập cổ',
  'pe_msk_cervical_extension': 'Duỗi cổ',
  'pe_msk_cervical_rotation': 'Xoay cổ',
  'pe_msk_lumbar_flexion': 'Gập thắt lưng',
  'pe_msk_lumbar_extension': 'Duỗi thắt lưng',
  'pe_msk_shoulder_height_symmetry': 'Đối xứng chiều cao vai',
  'pe_msk_shoulder_abduction_arom': 'Dạng vai chủ động',
  'pe_msk_hip_height_symmetry': 'Đối xứng chiều cao hông',
  'pe_msk_shoulder_impingement': 'Hội chứng va đập vai',
  'pe_msk_elbow_tenderness': 'Đau khuỷu tay',
  'pe_msk_wrist_tenderness': 'Đau cổ tay',
  'pe_msk_hip_tenderness': 'Đau hông',
  'pe_msk_knee_tenderness': 'Đau đầu gối',
  'pe_msk_ankle_tenderness': 'Đau mắt cá chân',
  'pe_msk_thomas_hip_test': 'Test Thomas hông',
  'pe_msk_faber_test': 'Test FABER',
  'pe_msk_knee_alignment': 'Thẳng hàng đầu gối',
  'pe_msk_patellar_reflex': 'Phản xạ bánh chè',
  'pe_msk_range_motion': 'Phạm vi chuyển động',
  'pe_msk_muscle_strength': 'Sức mạnh cơ',

  // === PHYSICAL EXAM - NEUROLOGICAL ===
  'pe_neuro_ulnar_nerve_sensory': 'Cảm giác thần kinh trụ',
  'pe_neuro_radial_nerve_sensory': 'Cảm giác thần kinh quay',
  'pe_neuro_medial_nerve_sensory': 'Cảm giác thần kinh giữa',
  'pe_neuro_pinprick_sensory': 'Cảm giác đâm kim (chân)',
  'pe_neuro_tandem_walk': 'Đi thẳng hàng',
  'pe_neuro_romberg': 'Test Romberg',
  'pe_neuro_reflexes': 'Phản xạ',
  'pe_neuro_coordination': 'Phối hợp',

  // === LIFESTYLE - NUTRITION ===
  'lifestyle_nutrition_appetite': 'Cảm giác thèm ăn',
  'lifestyle_nutrition_meals_per_day': 'Số bữa ăn mỗi ngày',
  'lifestyle_nutrition_water_intake': 'Lượng nước uống',
  'lifestyle_nutrition_alcohol_frequency': 'Tần suất uống rượu',
  'lifestyle_nutrition_caffeine_intake': 'Lượng caffeine',

  // === LIFESTYLE - MOVEMENT ===
  'lifestyle_movement_exercise_frequency': 'Tần suất tập thể dục',
  'lifestyle_movement_exercise_duration': 'Thời gian tập thể dục',
  'lifestyle_movement_exercise_intensity': 'Cường độ tập thể dục',
  'lifestyle_movement_steps_per_day': 'Số bước mỗi ngày',
  'lifestyle_movement_sedentary_hours': 'Giờ ngồi yên',

  // === LIFESTYLE - RECOVERY ===
  'lifestyle_recovery_sleep_hours': 'Giờ ngủ',
  'lifestyle_recovery_sleep_quality': 'Chất lượng giấc ngủ',
  'lifestyle_recovery_stress_level': 'Mức độ căng thẳng',
  'lifestyle_recovery_relaxation_time': 'Thời gian thư giãn',

  // === LIFESTYLE - EMOTIONS ===
  'lifestyle_emotions_mood_rating': 'Đánh giá tâm trạng',
  'lifestyle_emotions_anxiety_level': 'Mức độ lo âu',
  'lifestyle_emotions_social_support': 'Hỗ trợ xã hội',
  'lifestyle_emotions_life_satisfaction': 'Sự hài lòng cuộc sống',

  // === LIFESTYLE - ENVIRONMENT ===
  'lifestyle_environment_air_quality': 'Chất lượng không khí',
  'lifestyle_environment_noise_level': 'Mức độ tiếng ồn',
  'lifestyle_environment_safety_feeling': 'Cảm giác an toàn',
  'lifestyle_environment_green_space_access': 'Tiếp cận không gian xanh',

  // === IMAGING ===
  // EKG Parameters
  'imaging_ekg_rhythm': 'Nhịp điện tim',
  'imaging_ekg_rhythm_abnormality': 'Bất thường nhịp điện tim',
  'imaging_ekg_rate': 'Tần số điện tim',
  'imaging_ekg_heart_rate': 'Nhịp tim trên điện tim',
  'imaging_ekg_pr_interval': 'Khoảng PR',
  'imaging_ekg_axis_deviation': 'Lệch trục điện tim',
  'imaging_ekg_ventricular_hypertrophy': 'Phì đại thất',
  'imaging_ekg_conduction_abnormality': 'Bất thường dẫn truyền',
  'imaging_ekg_qrs_duration': 'Thời gian QRS',
  'imaging_ekg_qtc_interval': 'Khoảng QTc',
  'imaging_ekg_st_changes': 'Thay đổi ST',
  'imaging_ekg_t_wave_interval': 'Khoảng sóng T',
  'imaging_ekg_ectopy': 'Ngoại tâm thu',
  'imaging_ekg_interpretation': 'Kết luận điện tim',

  // Chest X-Ray Parameters
  'imaging_chest_xr_date': 'Ngày chụp X-quang ngực',
  'imaging_chest_xr_pleural_effusion': 'Tràn dịch màng phổi',
  'imaging_chest_xr_enlarged_pulmonary_artery': 'Giãn động mạch phổi',
  'imaging_chest_xr_enlarged_right_ventricle': 'Giãn thất phải',
  'imaging_chest_xr_flattened_diaphragm': 'Cơ hoành phẳng',
  'imaging_chest_xr_cardiothoracic_ratio_gt_50': 'Tỷ lệ tim-lồng ngực > 50%',
  'imaging_chest_xr_cavitary_lung_lesion': 'Tổn thương phổi có hốc',
  'imaging_chest_xr_widened_mediastinum': 'Giãn trung thất',
  'imaging_chest_xr_enlarged_hilar_lymph_nodes': 'Hạch rốn phổi to',
  'imaging_chest_xr_atelectasis': 'Xẹp phổi',
  'imaging_chest_xr_pulmonary_infiltrates': 'Thâm nhiễm phổi',
  'imaging_chest_xr_pulmonary_nodule_or_mass': 'Nốt hoặc khối phổi',
  'imaging_chest_xr_spinal_deviation': 'Lệch cột sống',
  'imaging_chest_xr_lytic_bone_lesion': 'Tổn thương xương tan',
  'imaging_chest_xr_heart_size': 'Kích thước tim trên X-quang ngực',
  'imaging_chest_xr_lung_fields': 'Trường phổi trên X-quang ngực',
  'imaging_chest_xr_mediastinum': 'Trung thất trên X-quang ngực',
  'imaging_chest_xr_pleural_spaces': 'Khoang màng phổi',
  'imaging_chest_xr_bones_soft_tissue': 'Xương và mô mềm',
  'imaging_chest_xr_findings': 'Kết quả X-quang ngực',
  'imaging_chest_xr_impression': 'Kết luận X-quang ngực',

  // Heart Ultrasound Parameters
  'imaging_heart_us_date': 'Ngày siêu âm tim',
  'imaging_heart_us_ejection_fraction': 'Phân suất tống máu',
  'imaging_heart_us_wall_motion': 'Vận động thành tim',
  'imaging_heart_us_valve_function': 'Chức năng van tim',
  'imaging_heart_us_chamber_size': 'Kích thước buồng tim',
  'imaging_heart_us_pericardial_effusion': 'Tràn dịch màng ngoài tim',
  'imaging_heart_us_left_ventricular_function': 'Chức năng thất trái',

  // Thyroid Ultrasound Parameters
  'imaging_thyroid_us_date': 'Ngày siêu âm tuyến giáp',
  'imaging_thyroid_us_right_lobe_size': 'Kích thước thùy phải tuyến giáp',
  'imaging_thyroid_us_left_lobe_size': 'Kích thước thùy trái tuyến giáp',
  'imaging_thyroid_us_isthmus_thickness': 'Độ dày eo tuyến giáp',
  'imaging_thyroid_us_echogenicity': 'Độ âm tuyến giáp',
  'imaging_thyroid_us_nodules_present': 'Có nốt tuyến giáp',
  'imaging_thyroid_us_largest_nodule_size': 'Kích thước nốt lớn nhất',
  'imaging_thyroid_us_nodule_composition': 'Thành phần nốt tuyến giáp',
  'imaging_thyroid_us_calcifications': 'Vôi hóa tuyến giáp',
  'imaging_thyroid_us_vascularity': 'Mạch máu tuyến giáp',
  'imaging_thyroid_us_lymph_nodes': 'Hạch lympho',
  'imaging_thyroid_us_tirads_score': 'Điểm TI-RADS',

  // Carotid Ultrasound Parameters
  'imaging_carotid_us_date': 'Ngày siêu âm động mạch cảnh',
  'imaging_carotid_us_l_cca_score': 'Điểm động mạch cảnh chung trái',
  'imaging_carotid_us_r_cca_score': 'Điểm động mạch cảnh chung phải',
  'imaging_carotid_us_l_bulb_score': 'Điểm bầu động mạch cảnh trái',
  'imaging_carotid_us_r_bulb_score': 'Điểm bầu động mạch cảnh phải',
  'imaging_carotid_us_l_ica_score': 'Điểm động mạch cảnh trong trái',
  'imaging_carotid_us_r_ica_score': 'Điểm động mạch cảnh trong phải',
  'imaging_carotid_us_l_cimt': 'Độ dày nội trung mạc trái',
  'imaging_carotid_us_r_cimt': 'Độ dày nội trung mạc phải',
  'imaging_carotid_us_plaque_presence': 'Có mảng xơ vữa',
  'imaging_carotid_us_surface_ulceration': 'Loét bề mặt mảng xơ vữa',
  'imaging_carotid_us_calcification_pattern': 'Mô hình vôi hóa',
  'imaging_carotid_us_l_ica_psv': 'Vận tốc tâm thu đỉnh động mạch cảnh trong trái',
  'imaging_carotid_us_r_ica_psv': 'Vận tốc tâm thu đỉnh động mạch cảnh trong phải',
  'imaging_carotid_us_l_ica_edv': 'Vận tốc cuối tâm trương động mạch cảnh trong trái',
  'imaging_carotid_us_r_ica_edv': 'Vận tốc cuối tâm trương động mạch cảnh trong phải',
  'imaging_carotid_us_vascular_age_imt': 'Tuổi mạch máu (IMT động mạch cảnh)',
  'imaging_carotid_us_vascular_age_cac': 'Tuổi mạch máu (điểm CAC)',

  // Spleen Ultrasound Parameters
  'imaging_spleen_us_craniocaudal_length': 'Chiều dài sọ-đuôi lách',
  'imaging_spleen_us_capsular_edge_nodularity': 'Nốt sần bờ bao lách',
  'imaging_spleen_us_parenchymal_echogenicity': 'Độ âm thực chất lách',
  'imaging_spleen_us_largest_lesion_diameter': 'Đường kính tổn thương lớn nhất',
  'imaging_spleen_us_splenic_vein_diameter': 'Đường kính tĩnh mạch lách',
  'imaging_spleen_us_psdr': 'Tỷ lệ tiểu cầu-đường kính lách (PSDR)',

  // Liver Ultrasound Parameters
  'imaging_liver_us_span': 'Kích thước gan siêu âm',
  'imaging_liver_us_craniocaudal_length': 'Chiều dài sọ-đuôi gan',
  'imaging_liver_us_capsular_edge_nodularity': 'Nốt sần bờ bao gan',
  'imaging_liver_us_echogenicity_steatosis': 'Độ âm gan (gan nhiễm mỡ)',
  'imaging_liver_us_portal_vein_diameter': 'Đường kính tĩnh mạch cửa',
  'imaging_liver_us_lirads': 'US-LIRADS',
  'imaging_liver_us_largest_lesion_diameter': 'Đường kính tổn thương lớn nhất',
  'imaging_liver_us_hepatic_vein_waveform': 'Dạng sóng tĩnh mạch gan',

  // Bladder Ultrasound Parameters
  'imaging_bladder_us_pre_void_volume': 'Thể tích bàng quang trước tiểu',
  'imaging_bladder_us_pvr_volume': 'Thể tích nước tiểu tồn dư',
  'imaging_bladder_us_wall_thickness': 'Độ dày thành bàng quang',
  'imaging_bladder_us_calculi_presence': 'Có sỏi bàng quang',
  'imaging_bladder_us_calculi_description': 'Mô tả sỏi bàng quang',
  'imaging_bladder_us_masses_presence': 'Có khối u bàng quang',
  'imaging_bladder_us_masses_description': 'Mô tả khối u bàng quang',
  'imaging_bladder_us_ureteral_jets': 'Dòng phun niệu quản',

  // Gallbladder Ultrasound Parameters
  'imaging_gallbladder_us_wall_thickness': 'Độ dày thành túi mật',
  'imaging_gallbladder_us_stones_present': 'Có sỏi túi mật',
  'imaging_gallbladder_us_sludge_present': 'Có cặn túi mật',
  'imaging_gallbladder_us_polyps_present': 'Có polyp túi mật',
  'imaging_gallbladder_us_distension': 'Giãn túi mật',

  // Kidney Ultrasound Parameters
  'imaging_kidney_us_right_size': 'Kích thước thận phải',
  'imaging_kidney_us_left_size': 'Kích thước thận trái',
  'imaging_kidney_us_echogenicity': 'Độ âm thận',
  'imaging_kidney_us_hydronephrosis': 'Giãn bể thận',
  'imaging_kidney_us_stones_present': 'Có sỏi thận',
  'imaging_kidney_us_masses_present': 'Có khối u thận'
};

/**
 * Get Vietnamese display name for a parameter
 * @param {string} parameterKey - The standardized parameter key
 * @returns {string} - Vietnamese display name
 * @throws {Error} - If parameter key is not found
 */
export function getParameterDisplayNameVi(parameterKey) {
  if (!parameterKey) {
    throw new Error('Parameter key is required');
  }

  const displayName = PARAMETER_DISPLAY_NAMES_VI[parameterKey];
  
  if (!displayName) {
    throw new Error(`Vietnamese display name not found for parameter: ${parameterKey}`);
  }

  return displayName;
}

/**
 * Check if Vietnamese translation exists for a parameter
 * @param {string} parameterKey - The standardized parameter key
 * @returns {boolean} - True if Vietnamese translation exists
 */
export function hasVietnameseTranslation(parameterKey) {
  return !!PARAMETER_DISPLAY_NAMES_VI[parameterKey];
}

/**
 * Get all available Vietnamese parameter translations
 * @returns {Object} - Object with parameter keys and Vietnamese display names
 */
export function getAllVietnameseParameterNames() {
  return { ...PARAMETER_DISPLAY_NAMES_VI };
}

export default PARAMETER_DISPLAY_NAMES_VI;
